import {
    Form,
    Input,
    Modal,
    Select,
    Button,
    Tag,
    Tooltip,
    Spin,
    notification,
    Space,
    Divider,
    Typography,
    Row,
    Col,
    Upload
} from "antd";
import {
    UploadOutlined,
    AudioOutlined,
    FileTextOutlined,
    SaveOutlined,
    CloseOutlined
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import { RcFile } from "antd/lib/upload";
import { confirmCloseModal } from './CModalConfirmClose';
import './CModalAddFile.styles.scss';

const { Text, Title } = Typography;
const { Option } = Select;

interface MyProps {
    open: boolean;
    bookId: string;
    setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
    onFinish: (values: any) => void;
    primaryVoice: any[];
}

const CModalAddFile = (props: MyProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [fileUploadLst, setFileUploadList] = useState<RcFile[]>([]);
    const [selectVoice, setSelectVoice] = useState<any[]>([]);
    const [hasChanges, setHasChanges] = useState(false);
    const [form] = Form.useForm();

    // Theo dõi thay đổi trong form
    useEffect(() => {
        const hasFormChanges = fileUploadLst.length > 0 || selectVoice.length > 0;
        setHasChanges(hasFormChanges);
    }, [fileUploadLst, selectVoice]);

    const handleFinishAddFile = async (values: any) => {
        setIsLoading(true);
        try {
            await props.onFinish({
                ...values,
                fileUploadLst,
                selectVoice
            });

            resetForm();
            props.setOpenModal(false);
        } catch (error) {
            notification.error({
                message: 'Lỗi',
                description: 'Có lỗi xảy ra khi tải lên file sách',
                placement: 'topRight',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        confirmCloseModal({
            hasChanges,
            onConfirm: () => {
                resetForm();
                props.setOpenModal(false);
            },
            title: 'Xác nhận đóng',
            content: 'Bạn có thay đổi chưa lưu. Bạn có chắc chắn muốn đóng modal thêm file sách?'
        });
    };

    const resetForm = () => {
        setFileUploadList([]);
        setSelectVoice([]);
        setHasChanges(false);
        form.resetFields();
    };

    return (
        <Modal
            open={props.open}
            title={
                <Space>
                    <UploadOutlined />
                    <span>Thêm file sách</span>
                </Space>
            }
            onCancel={handleCancel}
            footer={null}
            width={600}
            className="add-file-modal"
            destroyOnClose
            maskClosable={true}
        >
            <Spin spinning={isLoading} tip="Đang tải lên...">
                <div className="modal-content">
                    <Form
                        form={form}
                        layout="vertical"
                        className="add-file-form"
                        onFinish={handleFinishAddFile}
                    >
                        <Row gutter={[16, 12]}>
                            <Col span={24}>
                                <Form.Item
                                    label={
                                        <Space>
                                            <FileTextOutlined />
                                            <span>Tải file sách</span>
                                        </Space>
                                    }
                                    name="file"
                                    rules={[{ required: true, message: 'Vui lòng chọn file sách' }]}
                                >
                                    <Upload.Dragger
                                        maxCount={1}
                                        listType="picture"
                                        action={"https://localhost:3000/"}
                                        showUploadList={{
                                            showRemoveIcon: true,
                                        }}
                                        accept=".pdf, .epub"
                                        beforeUpload={(file) => {
                                            let tmplst = fileUploadLst;
                                            tmplst.push(file);
                                            setFileUploadList(tmplst);
                                            return false;
                                        }}
                                        progress={{
                                            strokeWidth: 3,
                                            strokeColor: {
                                                "0%": "#a9eab3",
                                                "100%": "#27CA40",
                                            },
                                            style: { top: 12 },
                                        }}
                                        className="file-upload-dragger"
                                    >
                                        <div className="upload-content">
                                            <UploadOutlined className="upload-icon" />
                                            <p className="upload-text">
                                                Click hoặc kéo file sách vào đây (file .epub)
                                            </p>
                                        </div>
                                    </Upload.Dragger>
                                </Form.Item>
                            </Col>

                            <Col span={24}>
                                <Form.Item
                                    label={
                                        <Space>
                                            <AudioOutlined />
                                            <span>Giọng đọc</span>
                                        </Space>
                                    }
                                    name="voices"
                                    rules={[{ required: true, message: 'Vui lòng chọn giọng đọc' }]}
                                >
                                    <Select
                                        mode="multiple"
                                        placeholder="Chọn giọng đọc (có thể chọn nhiều)"
                                        size="middle"
                                        className="form-select"
                                        onChange={(value: any) => {
                                            setSelectVoice(value);
                                        }}
                                        value={selectVoice}
                                        showSearch
                                        filterOption={(input, option: any) => {
                                            const label = option?.label || option?.children;
                                            return String(label).toLowerCase().includes(input.toLowerCase());
                                        }}
                                        tagRender={(props) => (
                                            <Tag
                                                closable={props.closable}
                                                onClose={props.onClose}
                                                className="custom-tag"
                                            >
                                                {props.label}
                                            </Tag>
                                        )}
                                    >
                                        {props.primaryVoice.map((item: any, index: any) => {
                                            return (
                                                <Option key={item.id} value={item.id}>
                                                    {item.name}
                                                </Option>
                                            )
                                        })}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider style={{ margin: '16px 0' }} />

                        <div className="modal-actions">
                            <Button
                                onClick={handleCancel}
                                className="cancel-button"
                                icon={<CloseOutlined />}
                            >
                                Hủy
                            </Button>
                            <Button
                                type="primary"
                                onClick={() => form.submit()}
                                loading={isLoading}
                                className="save-button"
                                icon={<SaveOutlined />}
                            >
                                Thêm file
                            </Button>
                        </div>
                    </Form>
                </div>
            </Spin>
        </Modal>
    );
};

export default CModalAddFile; 